import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiShoppingBag,
  FiDollarSign,
  FiMapPin,
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiFilter,
} from "react-icons/fi";

const AnalyticsContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const AnalyticsContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
  }

  .header-actions {
    display: flex;
    gap: 1rem;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid #2d5016;
  background: ${(props) => (props.primary ? "#2d5016" : "white")};
  color: ${(props) => (props.primary ? "white" : "#2d5016")};
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.primary ? "#1a3a0e" : "#2d5016")};
    color: white;
  }
`;

const TimeRangeSelector = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  .time-range-buttons {
    display: flex;
    gap: 0.5rem;

    button {
      padding: 0.5rem 1rem;
      border: 1px solid #e0e0e0;
      background: white;
      color: #666;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s ease;

      &.active {
        background: #2d5016;
        color: white;
        border-color: #2d5016;
      }

      &:hover:not(.active) {
        border-color: #2d5016;
        color: #2d5016;
      }
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
  }

  .content {
    flex: 1;

    .label {
      color: #666;
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
    }

    .value {
      font-size: 2rem;
      font-weight: 700;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .change {
      font-size: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      &.positive {
        color: #28a745;
      }

      &.negative {
        color: #dc3545;
      }
    }
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ChartHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const ChartContent = styled.div`
  padding: 1.5rem;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  background: #f8f9fa;
`;

const TablesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const TableContent = styled.div`
  padding: 1.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    font-weight: 600;
    color: #2d5016;
    font-size: 0.875rem;
  }

  td {
    color: #666;
    font-size: 0.875rem;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const TopVendorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  .rank {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #2d5016;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .vendor-info {
    flex: 1;

    h4 {
      font-size: 1rem;
      font-weight: 600;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .cuisine {
      color: #666;
      font-size: 0.875rem;
    }
  }

  .stats {
    text-align: right;

    .revenue {
      font-weight: 600;
      color: #2d5016;
      margin-bottom: 0.25rem;
    }

    .orders {
      color: #666;
      font-size: 0.875rem;
    }
  }
`;

const Analytics = () => {
  const [timeRange, setTimeRange] = useState("30d");

  const handleExportReport = () => {
    toast.success(
      "Exporting analytics report - This will download the report data"
    );
  };

  const handleRefreshData = () => {
    window.location.reload();
  };

  const timeRanges = [
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
    { value: "1y", label: "Last year" },
  ];

  // Default empty stats - will be populated with real data when API is ready
  const stats = [
    {
      icon: <FiUsers />,
      iconBg: "#2d5016",
      label: "Total Users",
      value: "0",
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiShoppingBag />,
      iconBg: "#4a7c59",
      label: "Total Orders",
      value: "0",
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiDollarSign />,
      iconBg: "#28a745",
      label: "Platform Revenue",
      value: "GYD $0",
      change: "0%",
      changeType: "neutral",
    },
    {
      icon: <FiTrendingUp />,
      iconBg: "#17a2b8",
      label: "Active Vendors",
      value: "0",
      change: "0",
      changeType: "neutral",
    },
  ];

  const topVendors = []; // Will be populated with real data when API is ready
  const recentOrders = []; // Will be populated with real data when API is ready

  return (
    <AnalyticsContainer>
      <AnalyticsContent>
        <PageHeader>
          <div>
            <h1>Analytics Dashboard</h1>
            <p>Platform performance and business insights</p>
          </div>
          <div className="header-actions">
            <ActionButton onClick={handleExportReport}>
              <FiDownload />
              Export Report
            </ActionButton>
            <ActionButton onClick={handleRefreshData}>
              <FiRefreshCw />
              Refresh Data
            </ActionButton>
          </div>
        </PageHeader>

        <TimeRangeSelector>
          <span style={{ fontWeight: "500", color: "#333" }}>Time Range:</span>
          <div className="time-range-buttons">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                className={timeRange === range.value ? "active" : ""}
                onClick={() => setTimeRange(range.value)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </TimeRangeSelector>

        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="icon" style={{ backgroundColor: stat.iconBg }}>
                {stat.icon}
              </div>
              <div className="content">
                <div className="label">{stat.label}</div>
                <div className="value">{stat.value}</div>
                <div className={`change ${stat.changeType}`}>
                  {stat.changeType === "positive" ? (
                    <FiTrendingUp />
                  ) : (
                    <FiTrendingDown />
                  )}
                  {stat.change} from last period
                </div>
              </div>
            </StatCard>
          ))}
        </StatsGrid>

        <ChartsGrid>
          <ChartCard>
            <ChartHeader>
              <h3>Revenue Trends</h3>
              <p>Platform revenue over time</p>
            </ChartHeader>
            <ChartContent>
              <div style={{ textAlign: "center" }}>
                <FiTrendingUp
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                />
                <p>
                  Chart visualization will be implemented with Chart.js or
                  similar library
                </p>
              </div>
            </ChartContent>
          </ChartCard>

          <ChartCard>
            <ChartHeader>
              <h3>Order Status Distribution</h3>
              <p>Current order status breakdown</p>
            </ChartHeader>
            <ChartContent>
              <div style={{ textAlign: "center" }}>
                <FiShoppingBag
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                />
                <p>Pie chart visualization will be implemented here</p>
              </div>
            </ChartContent>
          </ChartCard>
        </ChartsGrid>

        <TablesGrid>
          <TableCard>
            <TableHeader>
              <h3>Top Performing Vendors</h3>
              <p>Vendors ranked by revenue</p>
            </TableHeader>
            <TableContent>
              {topVendors.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#666",
                  }}
                >
                  <h3>No vendor data available</h3>
                  <p>
                    Top performing vendors will appear here once you have
                    vendors and orders.
                  </p>
                </div>
              ) : (
                topVendors.map((vendor, index) => (
                  <TopVendorCard key={vendor.id}>
                    <div className="rank">{index + 1}</div>
                    <div className="vendor-info">
                      <h4>{vendor.name}</h4>
                      <div className="cuisine">{vendor.cuisine}</div>
                    </div>
                    <div className="stats">
                      <div className="revenue">{vendor.revenue}</div>
                      <div className="orders">{vendor.orders} orders</div>
                    </div>
                  </TopVendorCard>
                ))
              )}
            </TableContent>
          </TableCard>

          <TableCard>
            <TableHeader>
              <h3>Recent Orders</h3>
              <p>Latest platform orders</p>
            </TableHeader>
            <TableContent>
              {recentOrders.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem",
                    color: "#666",
                  }}
                >
                  <h3>No orders yet</h3>
                  <p>
                    Recent orders will appear here once customers start placing
                    orders.
                  </p>
                </div>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Vendor</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.id}</td>
                        <td>{order.customer}</td>
                        <td>{order.vendor}</td>
                        <td>{order.amount}</td>
                        <td>
                          <span
                            style={{
                              padding: "0.25rem 0.75rem",
                              borderRadius: "20px",
                              fontSize: "0.75rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              backgroundColor:
                                order.status === "delivered"
                                  ? "#d4edda"
                                  : order.status === "confirmed"
                                    ? "#d1ecf1"
                                    : "#fff3cd",
                              color:
                                order.status === "delivered"
                                  ? "#155724"
                                  : order.status === "confirmed"
                                    ? "#0c5460"
                                    : "#856404",
                            }}
                          >
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </TableContent>
          </TableCard>
        </TablesGrid>
      </AnalyticsContent>
    </AnalyticsContainer>
  );
};

export default Analytics;
